import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogoFull } from '../components/Logo';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  Menu,
  X,
  Home,
  Heart,
  Settings,
  LogOut,
  PlusCircle,
  Shield,
  Building2,
  MessageSquare,
  CalendarCheck,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import NotificationBell from './NotificationBell';

export default function Navbar() {
  const { user, isAuthenticated, isOwner, isAdmin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Close mobile menu on route change.
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Scroll-aware glass intensification.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const navLinkClass =
    'link-underline text-sm lg:text-[15px] font-medium text-slate-600 transition-colors duration-300 hover:text-slate-950';

  return (
    <motion.nav
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.55, ease: [0.32, 0.72, 0, 1] }}
      className={`fixed inset-x-0 top-0 z-50 glass-nav ${scrolled ? 'glass-nav--scrolled' : ''}`}
      data-testid="navbar"
    >
      <div className="rh-container">
        <div className="flex h-16 md:h-[72px] items-center justify-between">
          <motion.div
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            transition={{ type: 'spring', stiffness: 400, damping: 22 }}
            className="shrink-0"
            data-testid="navbar-logo"
          >
            {/* Drop the tagline in the navbar so the wordmark can breathe and
                establish a clear weight hierarchy against the slim nav links. */}
            <LogoFull size="large" showTagline={false} />
          </motion.div>

          <div className="hidden items-center gap-6 md:flex">
            <Link to="/search" className={navLinkClass} data-testid="nav-search">
              Explore
            </Link>

            {isOwner && (
              <Link
                to="/owner/properties"
                className={navLinkClass}
                data-testid="nav-my-properties"
              >
                My Properties
              </Link>
            )}

            {isAuthenticated ? (
              <div className="flex items-center gap-4">
                {isOwner && (
                  <motion.div
                    whileHover={{ scale: 1.03, y: -1 }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
                  >
                    <Button
                      onClick={() => navigate('/property/new')}
                      className="btn-shine rounded-full bg-accent px-5 hover:bg-accent/90 shadow-glow-accent"
                      data-testid="nav-add-property"
                    >
                      <PlusCircle className="mr-2 h-4 w-4" />
                      Add Property
                    </Button>
                  </motion.div>
                )}

                <motion.button
                  onClick={() => navigate('/messages')}
                  whileHover={{ scale: 1.08 }}
                  whileTap={{ scale: 0.92 }}
                  transition={{ duration: 0.18 }}
                  className="p-2 rounded-full text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
                  data-testid="nav-messages"
                >
                  <MessageSquare className="h-5 w-5" />
                </motion.button>

                <NotificationBell />

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <motion.button
                      whileHover={{ scale: 1.06 }}
                      whileTap={{ scale: 0.94 }}
                      transition={{ duration: 0.18 }}
                      className="flex items-center gap-2 rounded-full p-1 transition-colors hover:bg-slate-100"
                      data-testid="user-menu-trigger"
                    >
                      <Avatar className="h-9 w-9 ring-2 ring-transparent transition-all duration-300 hover:ring-accent/30">
                        <AvatarImage src={user?.picture} alt={user?.name} />
                        <AvatarFallback className="bg-slate-200 font-medium text-slate-600">
                          {getInitials(user?.name)}
                        </AvatarFallback>
                      </Avatar>
                    </motion.button>
                  </DropdownMenuTrigger>

                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-3 py-2">
                      <p className="font-medium text-slate-900">{user?.name}</p>
                      <p className="text-base text-slate-500">{user?.email}</p>
                    </div>

                    <DropdownMenuSeparator />

                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                      <Home className="mr-2 h-4 w-4" />
                      Dashboard
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={() => navigate('/favorites')} data-testid="menu-favorites">
                      <Heart className="mr-2 h-4 w-4" />
                      Favorites
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={() => navigate('/bookings')} data-testid="menu-bookings">
                      <CalendarCheck className="mr-2 h-4 w-4" />
                      Bookings
                    </DropdownMenuItem>

                    <DropdownMenuItem onClick={() => navigate('/messages')} data-testid="menu-messages">
                      <MessageSquare className="mr-2 h-4 w-4" />
                      Messages
                    </DropdownMenuItem>

                    {isOwner && (
                      <DropdownMenuItem
                        onClick={() => navigate('/owner/properties')}
                        data-testid="menu-my-properties"
                      >
                        <Building2 className="mr-2 h-4 w-4" />
                        My Properties
                      </DropdownMenuItem>
                    )}

                    <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                      <Settings className="mr-2 h-4 w-4" />
                      Settings
                    </DropdownMenuItem>

                    {isAdmin && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                          <Shield className="mr-2 h-4 w-4" />
                          Admin Panel
                        </DropdownMenuItem>
                      </>
                    )}

                    <DropdownMenuSeparator />

                    <DropdownMenuItem
                      onClick={handleLogout}
                      className="text-red-600"
                      data-testid="menu-logout"
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Sign Out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/login')}
                  className="rounded-full text-sm lg:text-[15px] transition-all duration-200 hover:-translate-y-[1px]"
                  data-testid="nav-login"
                >
                  Sign In
                </Button>

                <motion.div
                  whileHover={{ scale: 1.03, y: -1 }}
                  whileTap={{ scale: 0.97 }}
                  transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
                >
                  <Button
                    onClick={() => navigate('/login')}
                    className="btn-shine rounded-full bg-slate-900 hover:bg-slate-800 text-sm lg:text-[15px]"
                    data-testid="nav-signup"
                  >
                    Get Started
                  </Button>
                </motion.div>
              </div>
            )}
          </div>

          <motion.button
            onClick={() => setMobileMenuOpen((prev) => !prev)}
            whileTap={{ scale: 0.9 }}
            className="p-2 text-slate-600 md:hidden"
            data-testid="mobile-menu-toggle"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <AnimatePresence mode="wait" initial={false}>
              {mobileMenuOpen ? (
                <motion.span
                  key="x"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="block"
                >
                  <X className="h-6 w-6" />
                </motion.span>
              ) : (
                <motion.span
                  key="menu"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="block"
                >
                  <Menu className="h-6 w-6" />
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              key="mobile-menu"
              initial={{ opacity: 0, height: 0, y: -8 }}
              animate={{ opacity: 1, height: 'auto', y: 0 }}
              exit={{ opacity: 0, height: 0, y: -6 }}
              transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
              className="overflow-hidden border-t border-slate-100 md:hidden"
            >
              <motion.div
                initial="hidden"
                animate="visible"
                variants={{
                  hidden: { opacity: 0 },
                  visible: { opacity: 1, transition: { staggerChildren: 0.04, delayChildren: 0.05 } },
                }}
                className="space-y-1 py-3"
              >
                {[
                  { to: '/search', label: 'Explore' },
                  isAuthenticated && { to: '/dashboard', label: 'Dashboard' },
                  isAuthenticated && { to: '/favorites', label: 'Favorites' },
                  isAuthenticated && { to: '/bookings', label: 'Bookings' },
                  isAuthenticated && { to: '/messages', label: 'Messages' },
                  isAuthenticated && { to: '/settings', label: 'Settings' },
                  isAuthenticated && isOwner && { to: '/owner/properties', label: 'My Properties' },
                  isAuthenticated && isAdmin && { to: '/admin', label: 'Admin Panel' },
                ]
                  .filter(Boolean)
                  .map((item) => (
                    <motion.div
                      key={item.to}
                      variants={{
                        hidden: { opacity: 0, x: -12 },
                        visible: { opacity: 1, x: 0 },
                      }}
                      transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
                    >
                      <Link
                        to={item.to}
                        className="block rounded-lg px-4 py-2.5 text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900"
                      >
                        {item.label}
                      </Link>
                    </motion.div>
                  ))}

                {isAuthenticated && isOwner && (
                  <motion.div
                    variants={{
                      hidden: { opacity: 0, x: -12 },
                      visible: { opacity: 1, x: 0 },
                    }}
                    transition={{ duration: 0.28 }}
                  >
                    <Link
                      to="/property/new"
                      className="block rounded-lg px-4 py-2.5 font-medium text-accent hover:bg-accent/10"
                    >
                      + Add Property
                    </Link>
                  </motion.div>
                )}

                {isAuthenticated ? (
                  <motion.button
                    variants={{
                      hidden: { opacity: 0, x: -12 },
                      visible: { opacity: 1, x: 0 },
                    }}
                    transition={{ duration: 0.28 }}
                    onClick={handleLogout}
                    className="block w-full rounded-lg px-4 py-2.5 text-left text-red-600 hover:bg-red-50"
                  >
                    Sign Out
                  </motion.button>
                ) : (
                  <motion.div
                    variants={{
                      hidden: { opacity: 0, y: 8 },
                      visible: { opacity: 1, y: 0 },
                    }}
                    transition={{ duration: 0.28 }}
                    className="space-y-2 px-4 pt-2"
                  >
                    <Button
                      onClick={() => navigate('/login')}
                      className="btn-shine w-full rounded-full bg-slate-900 hover:bg-slate-800"
                    >
                      Sign In / Sign Up
                    </Button>
                  </motion.div>
                )}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
}
